import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  Clock,
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  RotateCcw,
  Trash2,
  User,
  Wrench,
} from 'lucide-react';
import {
  usePostCrmCycleDamageTicket,
  type CrmTicket,
} from '@/api/bi/crm';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
  isOpenStatus,
} from '@/lib/crmStatus';
import { apiErrorMessage } from '@/lib/apiError';
import { fmt } from '@/lib/format';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';
import { CameraCaptureModal } from './CameraCaptureModal';

function resolveMediaUrl(file: string | null | undefined): string | null {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return file;
  const base = import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return `${base}${file.startsWith('/') ? '' : '/'}${file}`;
}

interface Props {
  ticket: CrmTicket;
  className?: string;
}

export function CycleTicketResolveCard({ ticket, className }: Props) {
  const [repairNote, setRepairNote] = useState('');
  const [repairedImage, setRepairedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [isPreviewLightboxOpen, setIsPreviewLightboxOpen] = useState(false);
  const [resolvedLightboxOpen, setResolvedLightboxOpen] = useState(false);

  const patchCycleDamage = usePostCrmCycleDamageTicket();

  const isResolvable = isOpenStatus(ticket.status);
  const isResolved = ticket.status === 'resolved';

  // Fallback to damaged cycle repair note if ticket resolution note is empty
  const resolvedNote =
    ticket.resolution_note?.trim() ||
    ticket.cycle_damages?.find((c) => c.repaired_note?.trim())?.repaired_note ||
    null;

  // Fallback to damaged cycle repaired image if ticket repaired_image is empty
  const rawResolvedImage =
    ticket.repaired_image ||
    ticket.cycle_damages?.find((c) => c.repaired_image)?.repaired_image ||
    null;
  const resolvedImageUrl = resolveMediaUrl(rawResolvedImage);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handlePhotoCaptured = (file: File) => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setRepairedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setRepairedImage(null);
  };

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedNote = repairNote.trim();
    if (!trimmedNote) {
      notify.error('A repair note is required to resolve this ticket.');
      return;
    }
    if (!repairedImage) {
      notify.error('A camera photo of the repaired cycle is required.');
      return;
    }

    try {
      await patchCycleDamage.mutateAsync({
        ticket_id: ticket.id,
        repaired_note: trimmedNote,
        repaired_image: repairedImage,
      });
      notify.success('Cycle damage ticket resolved successfully');
      setRepairNote('');
      handleRemovePhoto();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Failed to resolve cycle damage ticket'));
    }
  };

  const isSubmitDisabled =
    !repairNote.trim() || !repairedImage || patchCycleDamage.isPending;

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          {isResolvable ? (
            <>
              <Wrench className="h-4 w-4 text-primary" /> Resolve Damage Ticket
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 text-success" /> Ticket Resolution
            </>
          )}
        </CardTitle>
        <Badge tone={TICKET_STATUS_TONE[ticket.status]}>
          {TICKET_STATUS_LABEL[ticket.status] ?? ticket.status}
        </Badge>
      </CardHeader>

      <CardBody className="space-y-4 text-sm">
        {isResolvable ? (
          <form onSubmit={handleResolve} className="space-y-4">
            <p className="text-[11px] text-muted-foreground">
              Provide repair details and capture a photo from your device camera to mark this ticket as resolved.
            </p>

            {/* Repair Note Field */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="cycle-repair-note" className="text-xs font-medium">
                  Repair Note <span className="text-danger">*</span>
                </Label>
                <span className="text-[11px] text-muted-foreground">Required</span>
              </div>
              <Textarea
                id="cycle-repair-note"
                value={repairNote}
                onChange={(e) => setRepairNote(e.target.value)}
                placeholder="e.g. New rim fitted, brake cable replaced, aligned wheels..."
                rows={3}
                disabled={patchCycleDamage.isPending}
                className="text-xs resize-none"
              />
              {!repairNote.trim() && (
                <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 text-warning" />
                  Repair note is required to resolve this ticket.
                </p>
              )}
            </div>

            {/* Camera Photo Field (Camera Only) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium inline-flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5 text-primary" />
                  Repaired Cycle Photo <span className="text-danger">*</span>
                </Label>
                <span className="text-[10px] text-muted-foreground font-medium">
                  Camera Only
                </span>
              </div>

              {!repairedImage ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsCameraModalOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsCameraModalOpen(true);
                    }
                  }}
                  className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border hover:border-primary/60 rounded-lg cursor-pointer transition-colors bg-muted/10 hover:bg-primary/5 text-center group focus-ring"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-105 transition-transform mb-2">
                    <Camera className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors">
                    Take Photo with Camera
                  </span>
                  <span className="text-[11px] text-muted-foreground mt-0.5">
                    Strictly camera only — laptop webcam or mobile camera
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-card/60">
                  {imagePreviewUrl && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setIsPreviewLightboxOpen(true)}
                      className="relative h-14 w-14 shrink-0 rounded-md overflow-hidden border border-border cursor-pointer group"
                      title="Click to enlarge"
                    >
                      <img
                        src={imagePreviewUrl}
                        alt="Repaired cycle"
                        className="h-full w-full object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                        <Eye className="h-4 w-4" />
                      </div>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-foreground truncate">
                        {repairedImage.name}
                      </span>
                      <Badge tone="success" className="text-[10px] px-1.5 py-0">
                        Ready
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {fmt.bytes(repairedImage.size)} • Camera capture
                    </p>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px] gap-1"
                      onClick={() => setIsCameraModalOpen(true)}
                      title="Retake photo"
                    >
                      <RotateCcw className="h-3 w-3" /> Retake
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-danger"
                      onClick={handleRemovePhoto}
                      title="Remove photo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {!repairedImage && (
                <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 text-warning" />
                  Repaired cycle photo from camera is required to resolve this ticket.
                </p>
              )}
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={isSubmitDisabled}
              className="w-full gap-1.5"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {patchCycleDamage.isPending ? 'Resolving ticket…' : 'Resolve Ticket'}
            </Button>
          </form>
        ) : (
          <div className="space-y-3">
            <div
              className={cn(
                'rounded-lg border p-3.5 space-y-2.5',
                isResolved
                  ? 'border-success/30 bg-success/5 text-success-soft'
                  : 'border-border bg-muted/20 text-muted-foreground',
              )}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2
                  className={cn('h-4 w-4', isResolved ? 'text-success' : 'text-muted-foreground')}
                />
                <span className="font-semibold text-xs text-foreground">
                  {isResolved ? 'Ticket Resolved' : `Ticket ${TICKET_STATUS_LABEL[ticket.status] ?? ticket.status}`}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground pt-1 border-t border-border/50">
                {ticket.resolved_by && (
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Resolved by:{' '}
                      <strong className="font-medium text-foreground">
                        {ticket.resolved_by.name}
                      </strong>
                    </span>
                  </div>
                )}
                {ticket.resolved_at && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Resolved at:{' '}
                      <span className="text-foreground">
                        {fmt.datetime(ticket.resolved_at)} ({fmt.ago(ticket.resolved_at)})
                      </span>
                    </span>
                  </div>
                )}
              </div>

              {resolvedNote && (
                <div className="pt-2 border-t border-border/50 space-y-1">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Repair Note
                  </div>
                  <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed rounded-md bg-background/60 p-2 border border-border/40">
                    {resolvedNote}
                  </p>
                </div>
              )}

              {resolvedImageUrl && (
                <div className="pt-2 border-t border-border/50 space-y-1.5">
                  <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    <Camera className="h-3 w-3 text-success" /> Repaired Photo Evidence
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-md bg-background/60 border border-border/40">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setResolvedLightboxOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setResolvedLightboxOpen(true);
                        }
                      }}
                      className="group relative h-16 w-20 shrink-0 overflow-hidden rounded-md border border-border bg-black/10 cursor-pointer focus-ring"
                      title="Click to zoom repaired photo"
                    >
                      <img
                        src={resolvedImageUrl}
                        alt="Repaired cycle evidence"
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                        <Eye className="h-4 w-4" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="text-xs font-medium text-foreground">
                        Repair completion photo
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Click thumbnail to inspect full-size photo.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Camera Capture Modal */}
        <CameraCaptureModal
          open={isCameraModalOpen}
          onOpenChange={setIsCameraModalOpen}
          onCapture={handlePhotoCaptured}
        />

        {/* Thumbnail Preview Lightbox Modal */}
        {imagePreviewUrl && (
          <Dialog open={isPreviewLightboxOpen} onOpenChange={setIsPreviewLightboxOpen}>
            <DialogContent className="max-w-2xl p-4 bg-background">
              <DialogTitle className="text-sm font-semibold mb-2 inline-flex items-center gap-1.5">
                <ImageIcon className="h-4 w-4 text-primary" /> Captured Repair Photo
              </DialogTitle>
              <div className="rounded-lg overflow-hidden bg-black max-h-[70vh] flex items-center justify-center">
                <img
                  src={imagePreviewUrl}
                  alt="Repaired cycle full view"
                  className="max-h-[70vh] w-auto object-contain"
                />
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Resolved Photo Lightbox Modal */}
        {resolvedImageUrl && (
          <Dialog open={resolvedLightboxOpen} onOpenChange={setResolvedLightboxOpen}>
            <DialogContent className="max-w-2xl p-4 bg-background">
              <DialogTitle className="text-sm font-semibold mb-2 inline-flex items-center gap-1.5">
                <Camera className="h-4 w-4 text-success" /> Repaired Cycle Photo — Ticket #{ticket.id}
              </DialogTitle>
              <div className="rounded-lg overflow-hidden bg-black/5 border border-border max-h-[70vh] flex items-center justify-center">
                <img
                  src={resolvedImageUrl}
                  alt="Repaired cycle full view"
                  className="max-h-[70vh] w-auto object-contain"
                />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">
                  Ticket #{ticket.id} {resolvedNote ? `· ${resolvedNote}` : ''}
                </span>
                <a
                  href={resolvedImageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Open original <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardBody>
    </Card>
  );
}